/**
 * WalteroSkeleton - Branded Loading Skeleton Components
 * 
 * Provides consistent loading skeleton components for charts and other content
 * that maintain Waltero brand styling and create smooth loading experiences.
 */

import React from 'react';
import { Box, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * WalteroSkeleton - Base skeleton component with Waltero styling
 */
export const WalteroSkeleton = ({ 
  variant = 'rectangular',
  width = '100%',
  height = 40,
  animation = 'wave',
  sx = {},
  ...props 
}) => {
  const theme = useTheme();

  return (
    <Skeleton
      variant={variant}
      width={width}
      height={height}
      animation={animation}
      sx={{
        backgroundColor: theme.palette.waltero?.primary?.neutralBlue || '#d7e2e4',
        '&::after': {
          background: `linear-gradient(90deg, transparent, ${theme.palette.waltero?.primary?.backgroundBlue || '#edefef'}, transparent)`
        },
        borderRadius: variant === 'rectangular' ? 1 : undefined,
        ...sx
      }}
      {...props}
    />
  );
};

/**
 * WalteroChartSkeleton - Skeleton specifically designed for chart loading states
 */
export const WalteroChartSkeleton = ({ 
  height = 400, 
  width = '100%',
  showTitle = true,
  showLegend = false,
  showAxes = true,
  style = {},
  ...props 
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        width,
        height: `${height}px`,
        padding: 2,
        backgroundColor: 'transparent',
        ...style
      }}
      {...props}
    >
      {/* Chart Title Skeleton */}
      {showTitle && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <WalteroSkeleton width="200px" height="24px" />
        </Box>
      )}

      {/* Chart Area Container */}
      <Box sx={{ 
        position: 'relative', 
        height: showTitle ? 'calc(100% - 48px)' : '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Legend Skeleton */}
        {showLegend && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: 2, 
            mb: 2,
            flexWrap: 'wrap'
          }}>
            {[1, 2, 3].map((item) => (
              <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WalteroSkeleton variant="circular" width={12} height={12} />
                <WalteroSkeleton width="60px" height="16px" />
              </Box>
            ))}
          </Box>
        )}

        {/* Main Chart Area */}
        <Box sx={{ 
          flex: 1, 
          display: 'flex',
          position: 'relative'
        }}>
          {/* Y-Axis Skeleton */}
          {showAxes && (
            <Box sx={{ 
              width: '40px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              pr: 1,
              py: 2
            }}>
              {[1, 2, 3, 4, 5].map((item) => (
                <WalteroSkeleton key={item} width="32px" height="12px" />
              ))}
            </Box>
          )}

          {/* Chart Content Area */}
          <Box sx={{ 
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
          }}>
            {/* Chart Bars/Lines Skeleton */}
            <Box sx={{ 
              flex: 1,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-around',
              px: 2,
              pb: 1,
              gap: 1
            }}>
              {/* Simulated chart bars with varying heights */}
              {[0.6, 0.8, 0.4, 0.9, 0.7, 0.5, 0.8, 0.3].map((height, index) => (
                <WalteroSkeleton
                  key={index}
                  width="20px"
                  height={`${height * 60}%`}
                  sx={{
                    alignSelf: 'flex-end',
                    borderRadius: '4px 4px 0 0'
                  }}
                />
              ))}
            </Box>

            {/* X-Axis Skeleton */}
            {showAxes && (
              <Box sx={{ 
                display: 'flex',
                justifyContent: 'space-around',
                pt: 1,
                px: 2
              }}>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
                  <WalteroSkeleton key={item} width="24px" height="12px" />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * WalteroLineSkeleton - Skeleton for line charts
 */
export const WalteroLineSkeleton = ({ 
  height = 400, 
  width = '100%',
  showTitle = true,
  showLegend = false,
  style = {},
  ...props 
}) => {
  return (
    <Box
      sx={{
        width,
        height: `${height}px`,
        padding: 2,
        backgroundColor: 'transparent',
        ...style
      }}
      {...props}
    >
      {/* Chart Title Skeleton */}
      {showTitle && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <WalteroSkeleton width="200px" height="24px" />
        </Box>
      )}

      {/* Chart Area */}
      <Box sx={{ 
        position: 'relative', 
        height: showTitle ? 'calc(100% - 48px)' : '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Legend Skeleton */}
        {showLegend && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: 2, 
            mb: 2 
          }}>
            {[1, 2, 3].map((item) => (
              <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WalteroSkeleton width="16px" height="2px" />
                <WalteroSkeleton width="60px" height="16px" />
              </Box>
            ))}
          </Box>
        )}

        {/* Main Chart Area with wavy line simulation */}
        <Box sx={{ 
          flex: 1,
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Simulated line chart with curved skeleton */}
          <Box sx={{
            position: 'absolute',
            top: '20%',
            left: '10%',
            right: '10%',
            height: '60%',
            display: 'flex',
            alignItems: 'center'
          }}>
            <WalteroSkeleton
              width="100%"
              height="2px"
              sx={{
                borderRadius: '1px',
                transform: 'scaleY(8)',
                transformOrigin: 'center'
              }}
            />
          </Box>

          {/* Grid lines simulation */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: '10%',
            right: '10%',
            bottom: '15%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            {[1, 2, 3, 4].map((item) => (
              <WalteroSkeleton
                key={item}
                width="100%"
                height="1px"
                sx={{ opacity: 0.3 }}
              />
            ))}
          </Box>
        </Box>

        {/* Axes Skeletons */}
        <Box sx={{ display: 'flex', mt: 1 }}>
          <Box sx={{ width: '40px' }} />
          <Box sx={{ 
            flex: 1,
            display: 'flex',
            justifyContent: 'space-between',
            px: 2
          }}>
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <WalteroSkeleton key={item} width="32px" height="12px" />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * WalteroPieSkeleton - Skeleton for pie charts
 */
export const WalteroPieSkeleton = ({ 
  height = 400, 
  width = '100%',
  showTitle = true,
  showLegend = true,
  style = {},
  ...props 
}) => {
  return (
    <Box
      sx={{
        width,
        height: `${height}px`,
        padding: 2,
        backgroundColor: 'transparent',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...style
      }}
      {...props}
    >
      {/* Chart Title Skeleton */}
      {showTitle && (
        <Box sx={{ mb: 2 }}>
          <WalteroSkeleton width="200px" height="24px" />
        </Box>
      )}

      {/* Pie Chart Circle */}
      <Box sx={{ 
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <WalteroSkeleton
          variant="circular"
          width={Math.min(200, height * 0.5)}
          height={Math.min(200, height * 0.5)}
        />
      </Box>

      {/* Legend Skeleton */}
      {showLegend && (
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 2,
          mt: 2
        }}>
          {[1, 2, 3, 4].map((item) => (
            <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WalteroSkeleton variant="circular" width={12} height={12} />
              <WalteroSkeleton width="60px" height="16px" />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

/**
 * WalteroTableSkeleton - Skeleton for table loading states
 */
export const WalteroTableSkeleton = ({ 
  rows = 5,
  columns = 4,
  showHeader = true,
  height,
  width = '100%',
  style = {},
  ...props 
}) => {
  return (
    <Box
      sx={{
        width,
        height,
        ...style
      }}
      {...props}
    >
      {/* Table Header */}
      {showHeader && (
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          mb: 2,
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          {Array.from({ length: columns }).map((_, index) => (
            <WalteroSkeleton
              key={index}
              width={`${100 / columns}%`}
              height="20px"
            />
          ))}
        </Box>
      )}

      {/* Table Rows */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <Box key={rowIndex} sx={{ display: 'flex', gap: 2 }}>
            {Array.from({ length: columns }).map((_, colIndex) => (
              <WalteroSkeleton
                key={colIndex}
                width={`${100 / columns}%`}
                height="18px"
              />
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default WalteroSkeleton;